import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { ProfileFieldDiff } from '@/lib/laundryProfile';

interface ModificationDiffListProps {
  diffs: ProfileFieldDiff[];
  /** Column label for the base value (approved/previous). */
  fromLabel: string;
  /** Column label for the new value (requested/approved-new). */
  toLabel: string;
}

/** Readable comparison of the current/previous value vs the requested value. */
export default function ModificationDiffList({
  diffs,
  fromLabel,
  toLabel,
}: ModificationDiffListProps) {
  const { t } = useTranslation();

  if (diffs.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-background-300 bg-background-100/60 px-4 py-3 text-sm text-foreground-500">
        {t('settings.mod.noChanges')}
      </p>
    );
  }

  const valueClass = (multiline?: boolean) =>
    cn('text-sm text-foreground-800', multiline ? 'whitespace-pre-wrap break-words' : 'break-words');

  return (
    <>
      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-lg border border-background-200 md:block">
        <table className="w-full border-collapse text-start">
          <thead>
            <tr className="bg-background-100 text-xs text-foreground-600">
              <th className="px-4 py-2.5 text-start font-medium">{t('settings.mod.col.field')}</th>
              <th className="px-4 py-2.5 text-start font-medium">{fromLabel}</th>
              <th className="px-4 py-2.5 text-start font-medium">{toLabel}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {diffs.map((diff) => (
              <tr key={diff.key} className="align-top">
                <td className="w-40 px-4 py-3 text-sm font-medium text-foreground-950">
                  {t(diff.labelKey)}
                </td>
                <td className="px-4 py-3">
                  <span className={cn(valueClass(diff.multiline), 'text-foreground-500')}>
                    {diff.from}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={cn(valueClass(diff.multiline), 'font-medium')}>{diff.to}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="space-y-2.5 md:hidden">
        {diffs.map((diff) => (
          <li key={diff.key} className="rounded-lg border border-background-200 bg-background-50 p-3.5 shadow-xs">
            <p className="text-sm font-semibold text-foreground-950">{t(diff.labelKey)}</p>
            <div className="mt-2 space-y-1.5">
              <div>
                <span className="text-xs text-foreground-500">{fromLabel}</span>
                <p className={cn(valueClass(diff.multiline), 'mt-0.5 text-foreground-500')}>
                  {diff.from}
                </p>
              </div>
              <div>
                <span className="text-xs text-foreground-500">{toLabel}</span>
                <p className={cn(valueClass(diff.multiline), 'mt-0.5 font-medium')}>{diff.to}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}